import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@components/ui/button";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import { IndividualQuery } from "@utils/interface/index.interface";
import {
  CertificateWebhookConfigData,
  CertificateWebhookConfigFormData,
} from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { Terminal } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface CertificateWebhookFormProps {
  refetch?: () => void;
}

export const certificateWebhookSchema = z.object({
  name: z
    .string({ required_error: "Name is required" })
    .min(1, { message: "Name is required" }),
  webhook_url: z
    .string({ required_error: "Webhook URL is required" })
    .url({ message: "Enter a valid URL" })
    .refine((value) => value.startsWith("https://chat.googleapis.com/"), {
      message: "Must be a Google Chat webhook URL (chat.googleapis.com)",
    })
    .refine((value) => value.includes("key=") && value.includes("token="), {
      message: "The URL is missing its key or token — copy it again from Chat",
    }),
  is_active: z.boolean().optional(),
});

const CertificateWebhookForm = ({ refetch }: CertificateWebhookFormProps) => {
  const { searchParams, handleAction } = useHandleAction({});
  const modalItem = searchParams.get("modal-item");

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<CertificateWebhookConfigFormData>({
    mode: "onChange",
    resolver: zodResolver(certificateWebhookSchema),
    defaultValues: {
      name: "",
      webhook_url: "",
      is_active: true,
    },
  });

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<CertificateWebhookConfigData>
  >({
    endpoint: endpoint["certificate-webhook-config"],
    id: modalItem || "",
    key: `certificate-webhook-${modalItem}`,
    enabled: !!modalItem,
  });

  useEffect(() => {
    if (fetchData?.data && !fetchPending) {
      reset({
        name: fetchData.data.name || "",
        webhook_url: fetchData.data.webhook_url || "",
        is_active: !!fetchData.data.is_active,
      });
    } else if (!modalItem) {
      reset({ name: "", webhook_url: "", is_active: true });
    }
  }, [fetchData, fetchPending, modalItem, reset]);

  const { mutate, isPending } = usePostData();

  const onSubmit = (data: CertificateWebhookConfigFormData) => {
    mutate(
      {
        endpoint: endpoint["certificate-webhook-config"],
        method: modalItem ? METHODS.PATCH : METHODS.POST,
        data: modalItem ? { ...data, id: modalItem } : data,
      },
      {
        onSuccess: () => {
          refetch && refetch();
          handleAction({ type: "modal-close" });
          showToast({
            title: "Webhook",
            description: `Webhook ${modalItem ? "updated" : "created"}`,
            type: "success",
          });
        },
        onError: (error: any) => throwErrorAlert(error),
      }
    );
  };

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        label: "Name",
        type: "text",
        placeholder: "e.g. Certificates Google Chat",
        colSpan: 12,
        control,
        setValue,
        errors,
        className: "mb-[20px] min-w-full w-full",
      },
      {
        name: "webhook_url",
        label: "Webhook URL",
        type: "text",
        placeholder:
          "https://chat.googleapis.com/v1/spaces/XXXX/messages?key=…&token=…",
        colSpan: 12,
        control,
        setValue,
        errors,
        className: "mb-[20px] min-w-full w-full",
      },
      {
        name: "is_active",
        label: "Active",
        type: "checkbox",
        colSpan: 12,
        control,
        setValue,
        errors,
        className: "mb-[20px] min-w-full w-full",
      },
    ],
    [control, setValue, errors]
  );

  if (fetchPending && modalItem) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="row">
      <div className="col-12 mb-4">
        <Alert variant="default">
          <Terminal />
          <AlertTitle>
            Only <strong className="text-[#1DA077]">one</strong> webhook is used
            at a time
          </AlertTitle>
          <AlertDescription>
            The most recently updated active config wins. Anyone holding this URL
            can post to the space, so treat it as a password — it is masked in
            the list.
          </AlertDescription>
        </Alert>
      </div>
      {formFields.map(
        (field, index) =>
          field.visable !== false && (
            <div key={index} className={`col-md-${field.colSpan}`}>
              {getInputField(field)}
            </div>
          )
      )}
      <div className="col-12 flex justify-end gap-3 mt-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => handleAction({ type: "modal-close" })}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending
            ? "Processing..."
            : modalItem
            ? "Update Webhook"
            : "Create Webhook"}
        </Button>
      </div>
    </form>
  );
};

export default CertificateWebhookForm;
